
import { Auth } from "aws-amplify";
import { setIsLoggedIn, setUser, setUserAttributes } from "../store/authStore";
import { SignInEmailForm, SignUpEmailForm } from "../types/signupForm.types";
import { getFormattedDateFromDateString, GLOBAL_DATE_FORMAT, getIsoDateFromDateString } from "../utils/dateUtils";
import { LS } from "../data/localStoreKeys";
import { AwsConfigFetcher } from '../services/AwsConfigFetcher'; // Import your config fetcher


import { CognitoIdToken, CognitoUser } from 'amazon-cognito-identity-js';
import dayjs from "dayjs";
import { addCountryCodeToPhoneNumber } from "../utils/utils";
import { logger } from "../utils/sentryLogger";
import { CountryKeyManager, BrandIdentifier } from "../utils/environmentManager";
import { countries } from "../data/data";

/**
 * Cognito/Amplify error codes that represent expected user-driven outcomes,
 * not app bugs — matches are still returned to the caller, just not sent to
 * Sentry. Keyed by the Sentry `functionName` tag for that call site
 * (getAuthToken tags as 'getFreshIdToken', resetPasswordWithCode as
 * 'forgotPasswordSubmit' — kept as-is for continuity).
 *
 * Empty array = no confirmed expected codes yet, don't guess.
 * Groups below are reused across functions instead of duplicating lists.
 */
const CODE_VERIFICATION_ERRORS = ['CodeMismatchException', 'ExpiredCodeException'];
const PASSWORD_POLICY_ERRORS = ['InvalidPasswordException', 'PasswordHistoryPolicyViolationException'];
const RATE_LIMIT_ERRORS = ['TooManyFailedAttemptsException', 'LimitExceededException'];
const ALIAS_CONFLICT_ERRORS = ['AliasExistsException'];

// Auth.currentSession() (getAuthToken) rejects with this raw string when logged out.
const NO_CURRENT_USER = 'No current user';
// Auth.currentAuthenticatedUser() (getCurrentUser/getCurrentUserId) rewrites
// that same rejection to THIS string instead — verified in @aws-amplify/auth's
// source. Don't swap it for NO_CURRENT_USER, it never matches here.
const NOT_AUTHENTICATED = 'The user is not authenticated';

const EXPECTED_AUTH_ERRORS: Record<string, string[]> = {
  emailSignIn: ['NotAuthorizedException', 'UserNotFoundException', ...RATE_LIMIT_ERRORS],
  verifyEmailWithCode: [...CODE_VERIFICATION_ERRORS, ...RATE_LIMIT_ERRORS],
  resendConfirmationCode: [...RATE_LIMIT_ERRORS],
  sendForgotPasswordCode: ['UserNotFoundException'],
  forgotPasswordSubmit: ['InvalidParameterException', ...CODE_VERIFICATION_ERRORS, ...PASSWORD_POLICY_ERRORS, ...RATE_LIMIT_ERRORS], // resetPasswordWithCode
  completeTempPassword: [...PASSWORD_POLICY_ERRORS],
  getFreshIdToken: [NO_CURRENT_USER], // getAuthToken
  getCurrentUser: [NOT_AUTHENTICATED, 'NotAuthorizedException', 'UserNotConfirmedException'],
  getCurrentUserId: [NOT_AUTHENTICATED],
  updateUserAttributes: [...ALIAS_CONFLICT_ERRORS],
  sendCustomAttributeVerificationCode: [],
  verifyCustomAttribute: ['NotAuthorizedException', ...ALIAS_CONFLICT_ERRORS],
};

function captureAuthError(error: any, functionName: string, context: Record<string, any> = {}) {
  const code = typeof error === 'string' ? error : error?.code;
  if (!EXPECTED_AUTH_ERRORS[functionName]?.includes(code)) {
    logger.captureError(error as Error, {
      errorType: 'amplifyAuth',
      functionName,
      ...context,
    });
  }
}

export async function emailSignUp({
  firstName,
  lastName,
  phone,
  email,
  password,
  birthdate,
  country,
  language,
  termsAndMarketingAgreement,
  creditCheckConsent,
  address,
  city,
  postalCode,
}: SignUpEmailForm) {
  // Remove white space


  // Ensure config is loaded before proceeding
  // await ensureConfigInitialized();

  const configFetcher = AwsConfigFetcher.getInstance();

  // Generate username based on mode (similar to mobile app logic)
  let username: string;
  if (configFetcher.mode === 'legacy') {
    // In legacy mode, use email as username (remove whitespace)
    username = email ? email.replace(/\s/g, "") : "";
  } else {
    // In default mode, generate UUID as username
    username = crypto.randomUUID();
  }

  sessionStorage.setItem(LS.USERNAME, username);
  const cleanEmail = email ? email.replace(/\s/g, "") : "";

  const countryCode = country ? country.toUpperCase() : "";
  const countryName = countries.find((c) => c.code === country)?.name ?? "";

  console.log("counteryCode in emailSignUp: ", countryCode);


  const brand = CountryKeyManager.getInstance().getBrand();
  const addressAttributes = brand === BrandIdentifier.StorPro
    ? {
        address: address ?? "",
        "custom:city": city ?? "",
        "custom:postcode": postalCode ?? "",
      }
    : {};

  // TODO: handle country name properly
  try {
    const { user } = await Auth.signUp({
      username: username,
      password,
      attributes: {
        email: cleanEmail,
        name: firstName,
        family_name: lastName,
        phone_number: addCountryCodeToPhoneNumber(phone, country),
        birthdate: getIsoDateFromDateString(birthdate.startDate),
        "custom:username_type": "email",
        "custom:nationality": country,
        "custom:country": countryName,
        "custom:country_code": countryCode,
        "custom:preferred_language": language,
        ...addressAttributes,
      },
      autoSignIn: {
        // optional - enables auto sign in after user is confirmed
        enabled: true,
      },
    });
    return user;

  } catch (error: any) {
    console.log("emailSignUp error");
    console.log("error code: "+error.code);

    // Don't log expected user errors to Sentry
    const expectedErrors = [
      'UsernameExistsException'
    ];

    const isInvalidPhoneNumber = 
      error.code === 'InvalidParameterException' && 
      error.message?.toLowerCase().includes('phone');

    if (!expectedErrors.includes(error.code) && !isInvalidPhoneNumber) {
      logger.captureError(error, {
        errorType: 'amplifyAuth',
        functionName: 'emailSignUp',
        country: countryCode
      });
    }
    return {
      type: "error",
      error_code: error.code,
      message: error.message,
    };
  }
}

export async function getAuthToken() {
  try {
    const session = await Auth.currentSession();
    // console.log("getFreshIdToken:", JSON.stringify(session, null, 2));
    const idToken = session.getIdToken().getJwtToken(); // FRESH IdToken
    return idToken;
  } catch (error) {
    console.error("Failed to get fresh IdToken:", error);
    captureAuthError(error, 'getFreshIdToken');
    throw error;
  }
}

export async function verifyEmailWithCode({ email, code }: { email: string; code: string }) {
  const configFetcher = AwsConfigFetcher.getInstance();
  // Determine correct username based on mode
  const username =
    configFetcher.mode === 'legacy'
      ? email?.trim()
      : sessionStorage.getItem(LS.USERNAME) || '';

  if (!username) {
    const customError = new Error('Username is missing for verification');

    logger.captureError(customError, {
      errorType: 'amplifyAuth',
      functionName: 'verifyEmailWithCode',
    });

    return {
      type: 'error',
      message: customError.message,
    };
  }

  try {
    return await Auth.confirmSignUp(username, code);
  } catch (error: any) {
    console.log("verifyEmailWithCode");
    captureAuthError(error, 'verifyEmailWithCode', { username });
    return {
      type: "error",
      message: error.message,
    };
  }
}

export async function resendConfirmationCode(email: string) {


  const configFetcher = AwsConfigFetcher.getInstance();
  const username =
    configFetcher.mode === 'legacy'
      ? email?.trim()
      : sessionStorage.getItem(LS.USERNAME) || '';

      if (!username) {
        const customError = new Error('Username is missing for verification');

        logger.captureError(customError, {
          errorType: 'amplifyAuth',
          functionName: 'resendConfirmationCode',
        });

        return {
          type: 'error',
          message: customError.message,
        };
      }

  try {
    return await Auth.resendSignUp(username);
  } catch (error: any) {
    console.log("resendConfirmationCode");
    captureAuthError(error, 'resendConfirmationCode', { username });
    return {
      type: "error",
      message: error.message,
    };
  }
}


export async function sendForgotPasswordCode(email: string) {
  try {
    return await Auth.forgotPassword(email.toLowerCase());
  } catch (error: any) {
    console.log("sendForgotPasswordCode");
    captureAuthError(error, 'sendForgotPasswordCode');
    return {
      type: "error",
      message: error.message,
    };
  }
}

export async function resetPasswordWithCode({
  email,
  code,
  password,
}: {
  email: string;
  code: string;
  password: string;
}) {
  try {
    return await Auth.forgotPasswordSubmit(email, code, password);
  } catch (error: any) {
    console.log("resetPasswordWithCode");
    captureAuthError(error, 'forgotPasswordSubmit');
    return {
      type: "error",
      message: error.message,
      code: error.code,
    };
  }
}

// complete temp password challenge
export async function completeTempPassword(
  user: any,
  newPassword: string
) {
  try {
    return await Auth.completeNewPassword(user, newPassword);
  } catch (error: any) {
    captureAuthError(error, 'completeTempPassword');

    return {
      type: "error",
      message: error.message,
      code: error.code,
    };
  }
}

export async function emailSignIn({ email, password }: SignInEmailForm) {
  try {
    return await Auth.signIn(email, password);
  } catch (error: any) {
    console.log("emailSignIn Error", error);
    captureAuthError(error, 'emailSignIn');

    return {
      type: "error",
      message: error.message,
      code: error.code,
    };
  }
}

/**
 * This is basically the same interface as
 * { ICognitoUserSessionData } from 'amazon-cognito-identity-js', but with
 * plain strings as tokens. These are the raw tokens needed to authenticate
 * with Cognito. Our backend returns this object on successful completion of
 * login or signup with Vipps.
 */
export interface RawCognitoTokens {
    // AccessToken: string,  // We technically don't need the access token here
    RefreshToken:string,
    IdToken: string
}

function parseCognitoUsernameFromIdToken(idToken: string): string {
  // Note: We MUST use the username, not the sub. For legacy-style userpools
  // they are the same, but for new-style userpools they are absolutely not!
  return new CognitoIdToken({IdToken: idToken}).payload["cognito:username"];
}

/**
 *
 * @param tokens Use the Cognito CUSTOM_AUTH flow with our backend (which has
 * been configured to accept a valid refresh token (for the provided username)
 * as the challenge response) to "sign in" the user from a set of valid raw
 * Cognito tokens. Hypothetically we could just use those tokens directly, but
 * then we would either have to handle token refresh ourselves, or have
 * annoyingly short session expiry. We would rather have Amplify Auth construct
 * its whole complicated system to handle token refresh and user session
 * management, so from this point on, we can just run the app as though normal
 * sign in happened.
 */
export async function signInWithRawCognitoTokens(tokens: RawCognitoTokens): Promise<void> {
  const username = parseCognitoUsernameFromIdToken(tokens.IdToken);

  // Amplify Gen 1 for React Typescript v5 does the CUSTOM_AUTH flow if you
  // specify a username and no password. This is mentioned (with a kindof
  // ambiguous example) here:
  // https://docs.amplify.aws/gen1/react/prev/build-a-backend/auth/switch-auth/#custom-authentication-flow
  const user = await Auth.signIn(username);

  await Auth.sendCustomChallengeAnswer(user, tokens.RefreshToken);
}


export async function getCurrentUser() {
  try {
    return await Auth.currentAuthenticatedUser({ bypassCache: true });
  } catch (error) {
    //console.log("getCurrentUser Error", error);
    captureAuthError(error, 'getCurrentUser');

    return {
      type: "error",
    };
  }
}

export async function getUserIdToken() {
  try {
    return await Auth.currentSession()
    .then(currentSession => currentSession.getIdToken().getJwtToken())
  } catch (error) {
    console.log(error);
    throw error
  }
}

export async function getCurrentUserId(): Promise<string | null> {
  try {
    const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
    // Auth.currentAuthenticatedUser seems to return, rather annoyingly,
    // CognitoUser | any . If it happens that we got whatever thing other than
    // CognitoUser, then runtime issues will have to be caught below.
    // Remember that we must use the Cognito username, not the sub.
    return (user as CognitoUser).getUsername();
  } catch (error) {
    captureAuthError(error, 'getCurrentUserId');
    return null;
  }

}

export function setCurrentUser(user: any, dispatch: any) {
  try{
    if (user.type === "error") {
      console.log("user is not logged in");
    } else {
      const { attributes, signInUserSession } = user;

      dispatch(setIsLoggedIn(true));
      dispatch(setUserAttributes(attributes));
      dispatch(
        setUser({
          AccessToken: signInUserSession.accessToken.jwtToken,
          RefreshToken: signInUserSession.refreshToken.token,
          IdToken: signInUserSession.idToken.jwtToken,
          attributes: signInUserSession.idToken.payload,
        }),
      );

      // save cognito user in localstorage
      localStorage.setItem(LS.USER, JSON.stringify({
        AccessToken: signInUserSession.accessToken.jwtToken,
        RefreshToken: signInUserSession.refreshToken.token,
        IdToken: signInUserSession.idToken.jwtToken,
        attributes: signInUserSession.idToken.payload,
      }));

    }
  } catch (error){
    //console.log("setCurrentUser");
    logger.captureError(error as Error, {
      errorType: 'amplifyAuth',
      functionName: 'setCurrentUser'
    });
    return {
      type: "error",
    };
  }
}


export async function awsSignOut() {
  try {
    await Auth.signOut();
    sessionStorage.removeItem("loginType");

    // Clear user context from Sentry logger
    logger.clearUserContext();
    logger.info("User logged out");

    return {
      type: "success",
    };
  } catch (error: any) {
    console.log("awsSignOut");
    logger.captureError(error, {
      errorType: 'amplifyAuth',
      functionName: 'awsSignOut'
    });
    return {
      type: "error",
      message: error.message,
    };
  }
}

export async function updateUserAttributes(attributes: Record<any, any>) {
  const user = await Auth.currentAuthenticatedUser();
  try {
    await Auth.updateUserAttributes(user, attributes);
    return {
      type: "success",
      message: "User attributes updated successfully.",
    };
  } catch (error: any) {
    console.log("updateUserAttributes");
    captureAuthError(error, 'updateUserAttributes');
    return {
      type: "error",
      message: error.message,
    };
  }
}

// verify phone_number / any custom attribute
export async function sendCustomAttributeVerificationCode(attr = "phone_number") {
  return Auth.verifyCurrentUserAttribute(attr)
    .then(() => ({ type: "success", message: "A verification code was sent." }))
    .catch((e) => {
      console.log("sendCustomAttributeVerificationCode");
      captureAuthError(e, 'sendCustomAttributeVerificationCode');
      return { type: "error", message: e.message };
    });
}

export async function verifyCustomAttribute(code: string, attr = "phone_number") {
  return Auth.verifyCurrentUserAttributeSubmit(attr, code)
    .then(() => ({ type: "success", message: "Phone number verified successfully." }))
    .catch((e) => {
      console.log("verifyCustomAttribute");
      captureAuthError(e, 'verifyCustomAttribute');
      return { type: "error", message: e.message };
    });
}
